import { useState } from 'react'
import { Camera } from 'lucide-react'
import { Lightbox } from '../../../shared/ui/overlay/Lightbox/Lightbox'
import { SCREENSHOT_DIR, type GuideStepContent } from '../content/guideStep'

/**
 * Text left, phone screenshot right on desktop; stacked on a phone. A `wide` (desktop) screenshot
 * always goes under the text at full width. Tapping the shot enlarges it.
 */
export const GuideStep = ({ step }: { step: GuideStepContent }) => {
  const [open, setOpen] = useState(false)
  const frame = step.wide ? 'w-full max-w-3xl' : 'w-full max-w-[240px] shrink-0'

  return (
    <div
      className={
        step.wide ? 'flex flex-col gap-4' : 'flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-base text-gray-700">{step.text}</p>
        {step.note && (
          <aside className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-base font-bold text-amber-900">{step.note.title}</p>
            <p className="mt-1 text-base text-amber-900">{step.note.text}</p>
          </aside>
        )}
      </div>
      {!step.image && step.placeholder && (
        <div
          className={`${frame} ${step.wide ? 'aspect-video' : 'aspect-[9/19]'} flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500`}
        >
          <Camera size={24} aria-hidden />
          <p className="font-bold">Screenshot folgt</p>
          <p>{step.placeholder}</p>
          {step.screenshot && (
            <p className="mt-1 break-all rounded-lg bg-white px-2 py-1 font-mono text-xs text-gray-700">
              {SCREENSHOT_DIR}/<b>{step.screenshot}</b>
            </p>
          )}
        </div>
      )}
      {step.image && (
        <>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={`${step.imageAlt ?? step.title} vergrößern`}
            className={`${frame} overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm`}
          >
            <span className="relative block">
              <img src={step.image} alt={step.imageAlt ?? ''} loading="lazy" className="block w-full" />
              {step.focus?.map((f, i) => (
                <span
                  key={i}
                  aria-hidden
                  className="pointer-events-none absolute animate-pulse rounded-lg border-[3px] border-[#FFB400] shadow-[0_0_0_2px_rgba(255,255,255,0.8),0_0_14px_rgba(255,180,0,0.8)] motion-reduce:animate-none"
                  style={{ left: `${f.left}%`, top: `${f.top}%`, width: `${f.width}%`, height: `${f.height}%` }}
                />
              ))}
            </span>
          </button>
          <Lightbox
            open={open}
            items={[{ key: step.image, url: step.image, label: step.imageAlt ?? step.title }]}
            index={0}
            onIndexChange={() => undefined}
            onClose={() => setOpen(false)}
          />
        </>
      )}
    </div>
  )
}
